import { useRef, useEffect, useCallback, useState } from "react";
import { BubbleMenu } from "./BubbleMenu";
import { HighlightContextMenu } from "./HighlightContextMenu";
import { VariableRefSuggestion } from "./VariableRefSuggestion";
import { ContentEditor } from "./ContentEditor";
import { type PasteMode } from "../../lib/utils/pasteHandler";
import { applyFormatting, processHTMLContent } from "../../lib/utils/textFormatting";
import { useTextHighlightManager, useSelectionManager, useVariableReferenceManager } from "./hooks";
import { getCursorPosition, restoreCursorPosition, setCursorToEnd } from "./utils";
import { manualHighlightExclusionsService } from "@/lib/services/ManualHighlightExclusionsService";
import type { TypeItem } from "@/lib/stores/typesStore";
import type { ObjectItem } from "@/lib/stores/objects/types";
import { cleanVariableTokens } from "@/lib/utils/variableReferences";

interface TextBlockProps {
  id: string;
  content: string;
  placeholder?: string;
  isActive: boolean;
  /** Whether the editor is editable. When false, content cannot be modified. Defaults to true. */
  editable?: boolean;
  onContentChange: (id: string, content: string) => void;
  onEnter: (id: string, contentBeforeCursor?: string, contentAfterCursor?: string) => void;
  onBackspace: (id: string, currentContent?: string) => void;
  onFocus: (id: string) => void;
  onArrowUp: (id: string) => void;
  onArrowDown: (id: string) => void;
  onCommand: (id: string, command: string) => void;
  /**
   * How to handle paste:
   *  - "plain": paste as plain text (default) to avoid huge HTML payloads from apps like Figma/Docs
   *  - "basic-html": allow a minimal HTML subset via sanitizer
   */
  pasteMode?: PasteMode;
  /**
   * Whether to enable automatic highlighting
   */
  enableAutoHighlighting?: boolean;
  /**
   * Handler for when multiline text is pasted
   */
  onMultilinePaste?: (id: string, lines: string[]) => void;
  /**
   * Initial cursor position when the block becomes active.
   * - 'end': Position cursor at the end of content
   * - number: Position cursor at specific character offset
   * - undefined: Default behavior (browser default, usually start)
   */
  initialCursorPosition?: 'end' | number;
  /**
   * Callback to clear the initial cursor position after it's been applied
   */
  onCursorPositionApplied?: () => void;
  /**
   * Handler for when block metadata changes (e.g., highlight exclusions)
   */
  onMetadataChange?: (id: string, metadata: Record<string, any>) => void;
  /**
   * Handler for applying formatting across multiple blocks
   */
  onMultiBlockFormatting?: (command: string, value?: string) => void;
  /** Enable inline variable references ({{ key.name }} autocomplete and highlighting) */
  enableVariableReferences?: boolean;
  /** Available TypeItems for variable reference resolution */
  variableTypes?: TypeItem[];
  /** Available ObjectItems for variable reference resolution */
  variableObjects?: ObjectItem[];
}

export const TextBlock = ({
  id,
  content,
  placeholder = "Type '/' for commands",
  isActive,
  editable = true,
  onContentChange,
  onEnter,
  onBackspace,
  onFocus,
  onArrowUp,
  onArrowDown,
  onCommand,
  pasteMode = "plain",
  enableAutoHighlighting = true,
  onMultilinePaste,
  initialCursorPosition,
  onCursorPositionApplied,
  onMetadataChange,
  onMultiBlockFormatting,
  enableVariableReferences = false,
  variableTypes = [],
  variableObjects = [],
}: TextBlockProps) => {
  const editableRef = useRef<HTMLDivElement>(null);
  // Flag to skip the content-sync useEffect after a variable reference insertion
  // (prevents the useEffect from resetting innerHTML and destroying cursor position)
  const skipContentSyncRef = useRef(false);
  // Flag to skip decoration stripping on the next focus event.
  // Set when content is loaded while the block is already active (e.g., first block
  // on initial page load). The programmatic focus that follows should preserve
  // decoration so users see resolved variable values until they click to edit.
  const skipNextDecorationStripRef = useRef(false);
  
  // Use our custom hooks for managing different concerns
  const {
    stripExistingHighlights,
    cleanupInvalidHighlights,
    processTextWithHighlights,
    applyManualHighlight,
    removeHighlight,
    shouldApplyHighlights,
    highlightItems
  } = useTextHighlightManager();

  // Variable reference functionality
  const {
    autocomplete: varRefAutocomplete,
    suggestions: varRefSuggestions,
    closeAutocomplete: closeVarRefAutocomplete,
    handleInputForVarRef,
    insertReference,
    applyDecoration: applyVarRefDecoration,
    stripReferenceDecoration: stripVarRefDecoration,
  } = useVariableReferenceManager({
    types: enableVariableReferences ? variableTypes : [],
    objects: enableVariableReferences ? variableObjects : [],
  });
  
  // Callback for content changes from selection manager (e.g., when deleting selected text)
  const handleSelectionContentChange = useCallback((content: string) => {
    onContentChange(id, content);
  }, [id, onContentChange]);

  const {
    bubbleMenu,
    selectedText,
    hideBubbleMenu
  } = useSelectionManager(editableRef, handleSelectionContentChange);

  // State for highlight context menu
  const [highlightContextMenu, setHighlightContextMenu] = useState<{
    isVisible: boolean;
    position: { x: number; y: number };
    highlightId: string;
    highlightType: string;
    highlightName: string;
    text: string;
    isHighlighted: boolean;
  }>({
    isVisible: false,
    position: { x: 0, y: 0 },
    highlightId: "",
    highlightType: "",
    highlightName: "",
    text: "",
    isHighlighted: false,
  });

  useEffect(() => {
    if (isActive && editableRef.current) {
      // Use setTimeout to ensure the DOM is ready and the element is properly rendered
      setTimeout(() => {
        if (editableRef.current) {
          editableRef.current.focus();
          
          // Position cursor based on initialCursorPosition prop
          if (initialCursorPosition !== undefined) {
            if (initialCursorPosition === 'end') {
              setCursorToEnd(editableRef.current);
            } else if (typeof initialCursorPosition === 'number') {
              restoreCursorPosition(editableRef.current, initialCursorPosition);
            }
            // Notify parent that cursor position has been applied
            onCursorPositionApplied?.();
          }
        }
      }, 0);
    }
  }, [isActive, initialCursorPosition, onCursorPositionApplied]);

  // Track if this block is currently the active one (use ref to avoid stale closure)
  const isActiveRef = useRef(isActive);
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  // Apply automatic highlights when content or highlight items change
  const applyAutomaticHighlights = useCallback(() => {
    if (!editableRef.current || !enableAutoHighlighting || highlightItems.length === 0) {
      return;
    }
    
    try {
      // Clean up any invalid highlights first
      const hadInvalidHighlights = cleanupInvalidHighlights(editableRef.current);
      
      // Check if highlighting is needed
      if (!shouldApplyHighlights(editableRef.current) && !hadInvalidHighlights) {
        return;
      }
      
      // Store current cursor position
      const cursorPosition = getCursorPosition(editableRef.current);
      
      // Store the original HTML for comparison
      const originalHTML = editableRef.current.innerHTML;
      
      // Process highlights (pass block id for exclusion checking)
      const highlightedResult = processTextWithHighlights(editableRef.current, id);
      
      // Only update if there are actual highlights or if we cleaned up invalid ones
      if (highlightedResult.highlights.length > 0 || hadInvalidHighlights) {
        // Verify the highlighted HTML is valid before applying it
        // Check that list structures are preserved
        const hasLists = originalHTML.includes('<ul') || originalHTML.includes('<ol');
        
        if (hasLists) {
          // Count list items before and after
          const originalListItems = (originalHTML.match(/<li/g) || []).length;
          const highlightedListItems = (highlightedResult.highlightedHTML.match(/<li/g) || []).length;
          
          // If we lost list items, don't apply the highlights
          if (highlightedListItems < originalListItems) {
            console.warn('List structure would be corrupted by highlights, skipping highlight application');
            return;
          }
        }
        
        editableRef.current.innerHTML = highlightedResult.highlightedHTML;
        
        // Restore cursor position only if this block is still active
        // This prevents stealing focus from a newly created block after Enter is pressed
        if (cursorPosition > 0) {
          setTimeout(() => {
            // Check if this block is still active before restoring cursor
            // Using ref to get the latest value, not the stale closure value
            if (!isActiveRef.current) {
              return;
            }
            restoreCursorPosition(editableRef.current!, cursorPosition);
          }, 0);
        }
      }
    } catch (error) {
      console.error('Error applying automatic highlights:', error);
    }
  }, [id, enableAutoHighlighting, highlightItems, cleanupInvalidHighlights, shouldApplyHighlights, processTextWithHighlights]);

  useEffect(() => {
    // After a variable reference insertion, the DOM already has the correct
    // decorated content and cursor position. Skip this sync to avoid
    // resetting innerHTML (which would destroy the cursor position).
    if (skipContentSyncRef.current) {
      skipContentSyncRef.current = false;
      return;
    }

    if (editableRef.current && editableRef.current.innerHTML !== content) {
      // #region agent log
      if(import.meta.env.DEV){fetch('http://127.0.0.1:7242/ingest/8fd71a2b-62c7-4902-9a51-278ccbdfc26a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TextBlock.tsx:contentSyncEffect',message:'content sync triggered',data:{blockId:id,isActive,enableVarRefs:enableVariableReferences,contentLen:content?.length,innerHTMLLen:editableRef.current.innerHTML.length,contentSnippet:content?.substring(0,120),innerHTMLSnippet:editableRef.current.innerHTML.substring(0,120)},timestamp:Date.now(),hypothesisId:'H-B'})}).catch(()=>{});}
      // #endregion
      // Clean any corrupted variable tokens (e.g. highlight spans inside {{ }})
      // before processing so the editor works with clean {{ key.field }} tokens
      const cleanedContent = enableVariableReferences ? cleanVariableTokens(content) : content;

      // If the content was corrupted, save the cleaned version once to repair the stored data
      if (enableVariableReferences && cleanedContent !== content) {
        // #region agent log
        if(import.meta.env.DEV){fetch('http://127.0.0.1:7242/ingest/8fd71a2b-62c7-4902-9a51-278ccbdfc26a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TextBlock.tsx:cleanVariableTokens',message:'CORRUPTED TOKEN DETECTED - calling onContentChange',data:{blockId:id,contentSnippet:content?.substring(0,200),cleanedSnippet:cleanedContent?.substring(0,200)},timestamp:Date.now(),hypothesisId:'H-A'})}).catch(()=>{});}
        // #endregion
        onContentChange(id, cleanedContent);
      }

      // Load content as-is (preserving any existing highlights from the database)
      const processedContent = processHTMLContent(cleanedContent);
      editableRef.current.innerHTML = processedContent;
      
      // Apply highlights FIRST (synchronously) so the varRefRanges check in
      // processTextWithHighlights can see raw {{ }} tokens and skip them.
      // Then apply variable decoration AFTER highlights are done.
      if (enableAutoHighlighting && highlightItems.length > 0) {
        applyAutomaticHighlights();
      }

      // Apply variable reference decoration for visual display AFTER highlights.
      // Always apply on content load — even when the block is active (e.g., first
      // block on initial page load). The handleFocus callback will strip decoration
      // when the user explicitly clicks in; for programmatic focus we set a flag to
      // keep decoration visible.
      if (enableVariableReferences) {
        applyVarRefDecoration(editableRef.current);
        if (isActive) {
          // The isActive focus effect will fire a programmatic focus() right after
          // this effect. Tell handleFocus to preserve the decoration.
          skipNextDecorationStripRef.current = true;
        }
      }
    }
  }, [content, highlightItems.length, enableAutoHighlighting, applyAutomaticHighlights, enableVariableReferences, isActive, applyVarRefDecoration, onContentChange, id]);

  // Apply highlights when highlight items change (only if not currently focused/editing)
  useEffect(() => {
    if (enableAutoHighlighting && editableRef.current && !isActive && highlightItems.length > 0) {
      applyAutomaticHighlights();
    }
  }, [highlightItems, enableAutoHighlighting, isActive, applyAutomaticHighlights]);

  // Subscribe to exclusion changes and save to metadata
  useEffect(() => {
    if (!onMetadataChange) return;
    
    const saveExclusionsToMetadata = () => {
      const exclusions = manualHighlightExclusionsService.getBlockExclusions(id);
      if (exclusions.length > 0) {
        onMetadataChange(id, { highlightExclusions: exclusions });
      } else {
        // Clear metadata if no exclusions
        onMetadataChange(id, { highlightExclusions: undefined });
      }
    };
    
    // Subscribe to changes
    const unsubscribe = manualHighlightExclusionsService.subscribe(saveExclusionsToMetadata);
    
    // Save initial state
    saveExclusionsToMetadata();
    
    return unsubscribe;
  }, [id, onMetadataChange]);

  // Helper to find which block a node belongs to
  const findBlockIdFromNode = useCallback((node: Node | null): string | null => {
    if (!node) return null;
    
    let element = node.nodeType === Node.ELEMENT_NODE ? node as Element : node.parentElement;
    while (element) {
      const blockContainer = element.closest('[data-block-id]');
      if (blockContainer) {
        return blockContainer.getAttribute('data-block-id');
      }
      element = element.parentElement;
    }
    return null;
  }, []);

  const handleFormat = useCallback((command: string, value?: string) => {
    // Check if this is a multi-block selection
    const selection = window.getSelection();
    if (selection && !selection.isCollapsed && onMultiBlockFormatting) {
      const anchorBlockId = findBlockIdFromNode(selection.anchorNode);
      const focusBlockId = findBlockIdFromNode(selection.focusNode);
      
      // If selection spans multiple blocks, delegate to the multi-block handler
      if (anchorBlockId && focusBlockId && anchorBlockId !== focusBlockId) {
        console.log('🎨 Multi-block selection detected, delegating formatting');
        
        // Don't apply highlight commands across blocks (they need special handling)
        if (!command.includes('highlight')) {
          onMultiBlockFormatting(command, value);
          hideBubbleMenu();
          return;
        }
      }
    }

    if (command === 'highlight' && value) {
      try {
        const highlightData = JSON.parse(value);
        applyManualHighlight(highlightData, selectedText);
        
        // Update content (save with highlights)
        if (editableRef.current) {
          onContentChange(id, editableRef.current.innerHTML);
        }
      } catch (error) {
        console.error('Error applying highlight:', error);
      }
    } else if (command === 'manual-highlight' && value) {
      try {
        const data = JSON.parse(value);
        // Apply highlight manually (bypass exclusions)
        applyManualHighlight(data, selectedText);
        
        // Update content (with highlights)
        if (editableRef.current) {
          onContentChange(id, editableRef.current.innerHTML);
        }
        
        // Trigger a re-render to show the highlight
        if (enableAutoHighlighting) {
          setTimeout(() => applyAutomaticHighlights(), 100);
        }
      } catch (error) {
        console.error('Error applying manual highlight:', error);
      }
    } else if (command === 'manual-unhighlight' && value) {
      try {
        const data = JSON.parse(value);
        
        // Find and remove the specific highlight span by its ID and type
        if (editableRef.current) {
          const highlightSpans = editableRef.current.querySelectorAll('.text-highlight');
          let position = 0;
          let foundMatch = false;
          
          highlightSpans.forEach(span => {
            const spanId = span.getAttribute('data-highlight-id');
            const spanType = span.getAttribute('data-highlight-type');
            const spanText = span.textContent;
            
            // Count position for matching items (before we find our target)
            if (spanId === data.itemId && spanType === data.type && spanText === data.text) {
              if (!foundMatch) {
                // This is the one we want to remove
                foundMatch = true;
                
                // Add to exclusion service before removing
                manualHighlightExclusionsService.addExclusion(id, data.text, data.itemId, position);
                
                // Replace the highlight span with plain text
                const textNode = document.createTextNode(spanText || '');
                span.parentNode?.replaceChild(textNode, span);
              } else {
                // Count subsequent matches to maintain proper position tracking
                position++;
              }
            }
          });
          
          // Update content (with remaining highlights)
          onContentChange(id, editableRef.current.innerHTML);
        }
        
        // Trigger a re-render without this highlight
        if (enableAutoHighlighting) {
          setTimeout(() => applyAutomaticHighlights(), 100);
        }
      } catch (error) {
        console.error('Error removing manual highlight:', error);
      }
    } else if (command === 'remove-highlight') {
      removeHighlight(selectedText);
      
      // Update content (save with highlights)
      if (editableRef.current) {
        onContentChange(id, editableRef.current.innerHTML);
      }
    } else {
      // Handle other formatting commands (bold, italic, etc.)
      if (editableRef.current) {
        // Save current selection before any manipulations
        const selection = window.getSelection();
        const savedRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0).cloneRange() : null;
        
        // If contentEditable is false (due to selection mode), temporarily enable it
        const wasNotEditable = editableRef.current.contentEditable === 'false';
        if (wasNotEditable) {
          editableRef.current.contentEditable = 'true';
        }
        
        // Restore selection if it was saved
        if (savedRange && selection) {
          selection.removeAllRanges();
          selection.addRange(savedRange);
        }
        
        // Check if highlights are present
        const currentHTML = editableRef.current.innerHTML;
        const hasHighlights = currentHTML.includes('text-highlight');
        
        if (hasHighlights) {
          // Store the selected text and find its position in the text content
          if (!selectedText) {
            console.log('No selected text for formatting');
            // Restore contentEditable state
            if (wasNotEditable) {
              editableRef.current.contentEditable = 'false';
            }
            return;
          }
          
          // Get the text position before stripping highlights
          const textContent = editableRef.current.textContent || '';
          let textPosition = 0;
          
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            // Calculate text position by traversing from start
            const preRange = document.createRange();
            preRange.selectNodeContents(editableRef.current);
            preRange.setEnd(range.startContainer, range.startOffset);
            textPosition = (preRange.toString()).length;
          }
          
          // Temporarily strip highlights for clean formatting
          const cleanHTML = stripExistingHighlights(currentHTML);
          editableRef.current.innerHTML = cleanHTML;
          
          // Restore selection by finding the text at the calculated position
          setTimeout(() => {
            if (editableRef.current && selectedText) {
              try {
                const newTextContent = editableRef.current.textContent || '';
                
                // Find the selected text starting from the calculated position
                const searchStart = Math.max(0, textPosition - 10); // Allow small offset
                let targetIndex = newTextContent.indexOf(selectedText, searchStart);
                
                // If not found nearby, search from the beginning
                if (targetIndex === -1) {
                  targetIndex = newTextContent.indexOf(selectedText);
                }
                
                if (targetIndex >= 0) {
                  // Select the text using a tree walker
                  const range = document.createRange();
                  const sel = window.getSelection();
                  const walker = document.createTreeWalker(
                    editableRef.current,
                    NodeFilter.SHOW_TEXT,
                    null
                  );
                  
                  let node;
                  let charCount = 0;
                  let startNode = null;
                  let endNode = null;
                  let startOffset = 0;
                  let endOffset = 0;
                  
                  while ((node = walker.nextNode()) !== null) {
                    const nodeLength = node.textContent?.length || 0;
                    
                    // Find start position
                    if (!startNode && charCount + nodeLength > targetIndex) {
                      startNode = node;
                      startOffset = targetIndex - charCount;
                    }
                    
                    // Find end position
                    if (!endNode && charCount + nodeLength >= targetIndex + selectedText.length) {
                      endNode = node;
                      endOffset = (targetIndex + selectedText.length) - charCount;
                      break;
                    }
                    
                    charCount += nodeLength;
                  }
                  
                  if (startNode && endNode && sel) {
                    range.setStart(startNode, startOffset);
                    range.setEnd(endNode, endOffset);
                    sel.removeAllRanges();
                    sel.addRange(range);
                    
                    // Apply formatting to the restored selection
                    const normalizedContent = applyFormatting(command, value, editableRef.current);
                    onContentChange(id, normalizedContent);
                    
                    // Reapply highlights after formatting
                    if (enableAutoHighlighting && highlightItems.length > 0) {
                      setTimeout(() => {
                        applyAutomaticHighlights();
                      }, 100);
                    }
                    
                    // Restore contentEditable state if it was disabled
                    if (wasNotEditable && editableRef.current) {
                      editableRef.current.contentEditable = 'false';
                    }
                  } else {
                    console.log('Could not restore selection nodes');
                    // Restore contentEditable state if it was disabled
                    if (wasNotEditable && editableRef.current) {
                      editableRef.current.contentEditable = 'false';
                    }
                  }
                } else {
                  console.log('Could not find selected text in clean content');
                  // Restore contentEditable state if it was disabled
                  if (wasNotEditable && editableRef.current) {
                    editableRef.current.contentEditable = 'false';
                  }
                }
              } catch (error) {
                console.error('Error during selection restoration:', error);
                // Restore contentEditable state if it was disabled
                if (wasNotEditable && editableRef.current) {
                  editableRef.current.contentEditable = 'false';
                }
              }
            }
          }, 10);
        } else {
          // No highlights present, apply formatting directly
          // Store cursor position before formatting
          const cursorPosition = getCursorPosition(editableRef.current);
          
          const normalizedContent = applyFormatting(command, value, editableRef.current);
          onContentChange(id, normalizedContent);
          
          // Restore cursor position after content update
          setTimeout(() => {
            if (editableRef.current) {
              restoreCursorPosition(editableRef.current, cursorPosition);
            }
          }, 0);
          
          // Restore contentEditable state if it was disabled
          if (wasNotEditable && editableRef.current) {
            editableRef.current.contentEditable = 'false';
          }
        }
      }
    }
    
    hideBubbleMenu();
  }, [id, selectedText, applyManualHighlight, removeHighlight, stripExistingHighlights, onContentChange, hideBubbleMenu, enableAutoHighlighting, highlightItems.length, applyAutomaticHighlights, onMultiBlockFormatting, findBlockIdFromNode]);

  // Handle click on highlighted text to show context menu
  const handleHighlightClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as Element;
    if (target.classList.contains('text-highlight')) {
      // Don't show context menu if user is selecting text - let normal selection behavior continue
      const selection = window.getSelection();
      
      if (selection && !selection.isCollapsed && selection.toString().length > 0) {
        // Ensure the editor keeps focus and selection is preserved
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      
      e.preventDefault();
      e.stopPropagation();
      
      const highlightId = target.getAttribute('data-highlight-id') || "";
      const highlightType = target.getAttribute('data-highlight-type') || "";
      const highlightName = target.getAttribute('data-highlight-name') || "";
      const text = target.textContent || "";
      
      // Show context menu at click position
      setHighlightContextMenu({
        isVisible: true,
        position: { x: e.clientX, y: e.clientY },
        highlightId,
        highlightType,
        highlightName,
        text,
        isHighlighted: true,
      });
    }
  }, []);

  const handleCloseHighlightMenu = useCallback(() => {
    setHighlightContextMenu(prev => ({ ...prev, isVisible: false }));
  }, []);

  const handleHighlightFromMenu = useCallback(() => {
    // Apply highlight to the clicked text
    const { highlightId, highlightType, highlightName, text } = highlightContextMenu;
    
    const highlightData = {
      id: highlightId,
      type: highlightType,
      name: highlightName,
      keys: [text],
    };
    
    handleFormat('manual-highlight', JSON.stringify(highlightData));
  }, [highlightContextMenu, handleFormat]);

  const handleUnhighlightFromMenu = useCallback(() => {
    // Remove highlight from the clicked text
    const { highlightId, highlightType, text } = highlightContextMenu;
    
    const unhighlightData = {
      text,
      itemId: highlightId,
      type: highlightType,
    };
    
    handleFormat('manual-unhighlight', JSON.stringify(unhighlightData));
  }, [highlightContextMenu, handleFormat]);

  const handleFocus = useCallback((id: string) => {
    onFocus(id);
    
    // Don't strip highlights immediately on focus - wait for actual editing
    // This allows users to select text with highlights visible for formatting

    // If content was just loaded externally (e.g., initial page load) and the
    // block received a programmatic focus, keep decoration visible so users see
    // the resolved variable values. Decoration will be stripped when the user
    // explicitly clicks into the block or starts typing (via onInputCleanup).
    if (skipNextDecorationStripRef.current) {
      skipNextDecorationStripRef.current = false;
      return;
    }

    // Strip variable reference decoration on focus so user edits clean {{ }} tokens
    if (enableVariableReferences && editableRef.current) {
      const currentHTML = editableRef.current.innerHTML;
      const clean = stripVarRefDecoration(currentHTML);
      if (clean !== currentHTML) {
        editableRef.current.innerHTML = clean;
      }
    }
  }, [onFocus, enableVariableReferences, stripVarRefDecoration]);

  const handleBlur = useCallback(() => {
    // #region agent log
    if(import.meta.env.DEV){fetch('http://127.0.0.1:7242/ingest/8fd71a2b-62c7-4902-9a51-278ccbdfc26a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TextBlock.tsx:handleBlur',message:'block blur',data:{blockId:id,enableVarRefs:enableVariableReferences,innerHTMLBefore:editableRef.current?.innerHTML?.substring(0,120)},timestamp:Date.now(),hypothesisId:'H-B'})}).catch(()=>{});}
    // #endregion
    // Apply highlights when losing focus (perfect moment for highlighting)
    if (enableAutoHighlighting && editableRef.current && highlightItems.length > 0) {
      applyAutomaticHighlights();
      // Note: Tracking is handled by page-level useContentHighlighting hook
    }
    // Apply variable reference decoration when losing focus (visual only, no save)
    if (enableVariableReferences && editableRef.current) {
      closeVarRefAutocomplete();
      applyVarRefDecoration(editableRef.current);
      // Do NOT call onContentChange here — decoration is visual only
    }
  }, [enableAutoHighlighting, highlightItems.length, applyAutomaticHighlights, enableVariableReferences, closeVarRefAutocomplete, applyVarRefDecoration]);

  // Handle input cleanup to remove invalid highlights during typing
  const handleInputCleanup = useCallback((element: HTMLDivElement) => {
    if (enableAutoHighlighting && highlightItems.length > 0) {
      cleanupInvalidHighlights(element);
    }
    // Strip variable reference decoration from the DOM before content is saved
    // This ensures ContentEditor.handleInput always saves clean {{ }} tokens
    if (enableVariableReferences) {
      const currentHTML = element.innerHTML;
      const clean = stripVarRefDecoration(currentHTML);
      if (clean !== currentHTML) {
        element.innerHTML = clean;
      }
      handleInputForVarRef(element);
    }
  }, [enableAutoHighlighting, highlightItems.length, cleanupInvalidHighlights, enableVariableReferences, stripVarRefDecoration, handleInputForVarRef]);

  return (
    <div className="relative">
      <ContentEditor
        ref={editableRef}
        id={id}
        placeholder={placeholder}
        isActive={isActive}
        editable={editable}
        pasteMode={pasteMode}
        onContentChange={onContentChange}
        onEnter={onEnter}
        onBackspace={onBackspace}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onArrowUp={onArrowUp}
        onArrowDown={onArrowDown}
        onCommand={onCommand}
        onHighlightClick={handleHighlightClick}
        stripExistingHighlights={stripExistingHighlights}
        onMultilinePaste={onMultilinePaste}
        onInputCleanup={handleInputCleanup}
      />
      
      <BubbleMenu
        isVisible={bubbleMenu.isVisible}
        position={bubbleMenu.position}
        onFormat={handleFormat}
        selectedText={selectedText}
      />

      <HighlightContextMenu
        isVisible={highlightContextMenu.isVisible}
        position={highlightContextMenu.position}
        highlightId={highlightContextMenu.highlightId}
        highlightType={highlightContextMenu.highlightType}
        highlightName={highlightContextMenu.highlightName}
        text={highlightContextMenu.text}
        isHighlighted={highlightContextMenu.isHighlighted}
        onClose={handleCloseHighlightMenu}
        onHighlight={handleHighlightFromMenu}
        onUnhighlight={handleUnhighlightFromMenu}
      />

      {enableVariableReferences && (
        <>
          <style>{`
            /* Variable reference styles for document editor */
            .var-ref {
              display: inline;
              background-color: hsl(220 90% 56% / 0.12);
              color: hsl(220 90% 40%);
              border: 1px solid hsl(220 90% 56% / 0.25);
              border-radius: 4px;
              padding: 0 4px;
              font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace;
              font-size: 0.85em;
              white-space: nowrap;
              cursor: default;
              transition: background-color 0.15s ease, border-color 0.15s ease;
            }
            .var-ref:hover {
              background-color: hsl(220 90% 56% / 0.2);
              border-color: hsl(220 90% 56% / 0.4);
            }
            .var-ref-unresolved {
              background-color: hsl(0 80% 56% / 0.1);
              color: hsl(0 80% 40%);
              border-color: hsl(0 80% 56% / 0.25);
            }
            .var-ref-unresolved:hover {
              background-color: hsl(0 80% 56% / 0.18);
              border-color: hsl(0 80% 56% / 0.4);
            }
          `}</style>
          <VariableRefSuggestion
            isOpen={varRefAutocomplete.isOpen}
            position={varRefAutocomplete.position}
            items={varRefSuggestions}
            onSelect={(refString) => {
              if (editableRef.current) {
                insertReference(refString, editableRef.current);
                // Save clean content (with raw {{ }} tokens, no decoration markup)
                const cleanHTML = stripVarRefDecoration(editableRef.current.innerHTML);
                // Skip the next content-sync useEffect so it doesn't reset
                // the decorated innerHTML and destroy the cursor position
                skipContentSyncRef.current = true;
                onContentChange(id, cleanHTML);
              }
            }}
            onClose={closeVarRefAutocomplete}
          />
        </>
      )}
    </div>
  );
}; 